/**
 * USVI Pirate's Guild — Events constants (Phase 5).
 *
 * Single source of truth for the `events` Firestore collection's enums and
 * the labels rendered on traveler/business UIs.
 */

import { ISLANDS } from "./categories";

// ---------------------------------------------------------------------------
// Event types
// ---------------------------------------------------------------------------

export const EVENT_TYPE_KEYS = Object.freeze({
  PUBLIC: "public",
  FEATURED: "featured",
  SPONSORED: "sponsored",
  TICKETED: "ticketed",
  VIP: "vip",
  NETWORKING: "networking",
  TREASURE_HUNT: "treasure-hunt",
  PARTNER: "partner",
});

export const EVENT_TYPES = Object.freeze([
  { key: EVENT_TYPE_KEYS.PUBLIC, label: "Public" },
  { key: EVENT_TYPE_KEYS.FEATURED, label: "Featured" },
  { key: EVENT_TYPE_KEYS.SPONSORED, label: "Sponsored" },
  { key: EVENT_TYPE_KEYS.TICKETED, label: "Ticketed" },
  { key: EVENT_TYPE_KEYS.VIP, label: "VIP" },
  { key: EVENT_TYPE_KEYS.NETWORKING, label: "Networking" },
  { key: EVENT_TYPE_KEYS.TREASURE_HUNT, label: "Treasure Hunt" },
  { key: EVENT_TYPE_KEYS.PARTNER, label: "Partner" },
]);

export const ALL_EVENT_TYPE_KEYS = EVENT_TYPES.map((t) => t.key);

// ---------------------------------------------------------------------------
// Event statuses (lifecycle)
// ---------------------------------------------------------------------------

export const EVENT_STATUS_KEYS = Object.freeze({
  DRAFT: "draft",         // business is editing
  PENDING: "pending",     // awaiting admin approval (sponsored/featured)
  PUBLISHED: "published", // visible to travelers
  CANCELLED: "cancelled",
  COMPLETED: "completed", // automatically set after endAt
});

export const EVENT_STATUSES = Object.freeze([
  { key: EVENT_STATUS_KEYS.DRAFT, label: "Draft" },
  { key: EVENT_STATUS_KEYS.PENDING, label: "Pending Review" },
  { key: EVENT_STATUS_KEYS.PUBLISHED, label: "Published" },
  { key: EVENT_STATUS_KEYS.CANCELLED, label: "Cancelled" },
  { key: EVENT_STATUS_KEYS.COMPLETED, label: "Completed" },
]);

// ---------------------------------------------------------------------------
// Pricing models
// ---------------------------------------------------------------------------

export const EVENT_PRICING_KEYS = Object.freeze({
  FREE: "free",
  RSVP: "rsvp",         // free but headcount tracked
  TICKETED: "ticketed", // paid via Stripe
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getEventTypeLabel(key) {
  return EVENT_TYPES.find((t) => t.key === key)?.label || key;
}

export function getEventStatusLabel(key) {
  return EVENT_STATUSES.find((s) => s.key === key)?.label || key;
}

// ---------------------------------------------------------------------------
// Recurrence (rule-string + on-demand expansion model)
// ---------------------------------------------------------------------------

export const RECURRENCE_FREQ = Object.freeze({
  ONE_TIME: "ONE_TIME",
  DAILY: "DAILY",
  WEEKLY: "WEEKLY",
  BIWEEKLY: "BIWEEKLY",
  MONTHLY_BY_DATE: "MONTHLY_BY_DATE",
  MONTHLY_BY_WEEKDAY: "MONTHLY_BY_WEEKDAY",
});

export const RECURRENCE_FREQ_OPTIONS = Object.freeze([
  { key: RECURRENCE_FREQ.ONE_TIME, label: "One-time" },
  { key: RECURRENCE_FREQ.DAILY, label: "Daily" },
  { key: RECURRENCE_FREQ.WEEKLY, label: "Weekly" },
  { key: RECURRENCE_FREQ.BIWEEKLY, label: "Every 2 weeks" },
  { key: RECURRENCE_FREQ.MONTHLY_BY_DATE, label: "Monthly (by date)" },
  { key: RECURRENCE_FREQ.MONTHLY_BY_WEEKDAY, label: "Monthly (by weekday)" },
]);

// Two-letter day codes used inside recurrence.byDay arrays. Matches RFC-5545
// shorthand so we can drop in `rrule` later without a data migration.
export const DAYS_OF_WEEK = Object.freeze([
  { key: "SU", label: "Sun", index: 0 },
  { key: "MO", label: "Mon", index: 1 },
  { key: "TU", label: "Tue", index: 2 },
  { key: "WE", label: "Wed", index: 3 },
  { key: "TH", label: "Thu", index: 4 },
  { key: "FR", label: "Fri", index: 5 },
  { key: "SA", label: "Sat", index: 6 },
]);

export const DAY_KEY_TO_INDEX = Object.freeze(
  DAYS_OF_WEEK.reduce((acc, d) => {
    acc[d.key] = d.index;
    return acc;
  }, {}),
);

export const DAY_INDEX_TO_KEY = Object.freeze(
  DAYS_OF_WEEK.reduce((acc, d) => {
    acc[d.index] = d.key;
    return acc;
  }, {}),
);

// ---------------------------------------------------------------------------
// Event intention (free-form hint, used for analytics + template lookup)
// ---------------------------------------------------------------------------

export const EVENT_INTENTIONS = Object.freeze([
  "happy-hour",
  "themed-night",
  "brunch",
  "tasting",
  "class",
  "live-music",
  "trivia",
  "karaoke",
  "open-mic",
  "dj-night",
  "watch-party",
  "yoga",
  "sail",
  "charter",
  "bonfire",
  "treasure-hunt",
  "networking",
  "vip",
  "celebration",
  "holiday",
  "ticketed-special",
  "custom",
]);

// Re-export ISLANDS so consumers only need a single import.
export { ISLANDS };
